import { get } from 'lodash';
import {
	formatDynamicTextValue,
	parseDynamicTextHelperExpression,
} from './dynamicTextFormatHelpers';
import { getMergeTagTranslation } from './mergeTagDictionary';

export interface AttachedListingData {
	id?: string;
	address?: string;
	address1?: string;
	city?: string;
	state?: string;
	stateProvince?: string;
	postalCode?: string;
	zip?: string;
	list_price?: number | string;
	listPrice?: number | string;
	beds?: number | string;
	baths?: number | string;
	sq_foot?: number | string;
	sqFoot?: number | string;
	image_data?: string[];
	image?: string;
	imagePath?: string;
	imageUrl?: string;
	photo?: string;
	listingImage?: string;
	mainImage?: string;
	primaryImage?: string;
	url?: string;
	listingurl?: string;
	[key: string]: unknown;
}

// Matches both {{listing.x}} and {{user.x}} — capture group 2 keeps the full
// "listing."/"user." prefix so the caller can dispatch to the right resolver.
const ATTACHED_DYNAMIC_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*((?:listing|user)\.[a-zA-Z0-9_.]+)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
// {{else}} is optional: a formatted tag with no fallback serializes as
// {{#if key}}{{formatDynamicText key ...}}{{/if}} (MergeTagBadge.revert), and a
// mandatory-else pattern let that variant slip through BOTH if-passes — the
// generic pass below then resolved the inner helper but left the {{#if}}
// wrapper standing, which a downstream Handlebars compose (EmailPreview.js, the
// send pipeline) re-evaluated against a context with no listing/user key and
// silently dropped the already-resolved value.
const ATTACHED_LISTING_IF_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g;
// Same {{#if listing.x}}/{{#if user.x}} construct as above, but for a true-branch
// that isn't a single bare tag — e.g. a whole <a href="{{listing.url}}">{{listing.url}}
// </a> — which the narrow pattern above structurally can't match (it requires
// \s*{{...}}\s* exactly). Runs after it, so it only ever sees {{#if}} blocks the
// narrow pass left behind; a caller two-pass-composing this string with its own
// Handlebars afterward (e.g. the email preview panel) would otherwise re-evaluate
// this exact condition against a context that never has a listing/user key, silently
// discarding an already-resolved branch. See EmailPreview.js conversation for the
// concrete symptom.
const ATTACHED_LISTING_IF_HTML_PATTERN =
	/\{\{#if\s+((?:listing|user)\.[\w.-]+)\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g;
const GENERIC_MERGE_TAG_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

// Round-trip marker for a resolved inline listing token. Rich-text content is
// synced back to storage from the live contenteditable DOM on blur — a bare
// resolved value ("240 7th Street") is indistinguishable from hand-typed text
// by then, so the original tag is lost for good. Locking the resolved value
// in a non-editable span keeps the field's tag KEY attached to the DOM node,
// so it survives the round trip and a bare {{listing.x}} tag can be rebuilt
// for production. The key only (not the original {{...}} text) is stored
// deliberately: MergeTagBadge.transform (packages/bay-email-editor/src/utils/
// MergeTagBadge.ts) runs a catch-all /\{\{([\s\S]+?)\}\}/g replace over a
// node's raw innerHTML STRING whenever a merge tag is inserted via the
// toolbar, with no notion of "this is inside an attribute, skip it" — any
// literal {{ stored in an attribute gets matched and corrupted. A plain field
// key like "listing.address" never contains { or }, so it's structurally
// invisible to that regex (or any other {{...}}-based scanner).
const LOCKED_SPAN_PATTERN =
	/<span\b(?=[^>]*\bdata-dynamic-locked="true")[^>]*\bdata-dynamic-tag="([^"]*)"[^>]*>[\s\S]*?<\/span>/gi;

// A wrapper <span> can never legally sit inside an attribute value (e.g.
// href="{{listing.url}}") — checked by POSITION, not field name, so any
// listing field used as a link is covered, not just `url`. The same field's
// tag sitting in ordinary text content (e.g. a visible {{listing.url}}
// label) has no such restriction and should still lock.
function isInsideAttributeValue(fullString: string, matchIndex: number) {
	const before = fullString.slice(0, matchIndex);
	const lastOpenTag = before.lastIndexOf('<');
	const lastCloseTag = before.lastIndexOf('>');
	// No tag currently open at this position — plain text content.
	if (lastOpenTag <= lastCloseTag) return false;
	const withinTag = before.slice(lastOpenTag);
	// An odd number of quotes since the tag opened means we're inside a
	// quoted attribute value right now.
	return (withinTag.match(/"/g) || []).length % 2 === 1;
}

function escapeAttr(value: string) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function lockResolvedValue(tagKey: string, resolvedHtml: string) {
	return `<span data-dynamic-tag="${escapeAttr(tagKey)}" data-dynamic-locked="true" contenteditable="false">${resolvedHtml}</span>`;
}

// Inverse of lockResolvedValue — converts a previously-locked span back into
// a bare {{listing.x}} tag from its stored key, so a design edited under
// this fix still emits something send-time-resolvable in production instead
// of the locked markup/value.
export function restoreAttachedListingMergeTags(template: string | undefined) {
	if (!template) return template || '';
	return template.replace(LOCKED_SPAN_PATTERN, (_match, tagKey: string) => `{{${tagKey}}}`);
}

const isDynamicKey = (key: string) => key.startsWith('listing.') || key.startsWith('user.');

function toNumber(value: unknown) {
	if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
	if (typeof value === 'string' && value.trim() !== '') {
		const parsed = Number(value.replace(/[$,]/g, ''));
		return Number.isFinite(parsed) ? parsed : undefined;
	}
	return undefined;
}

function formatCurrency(value: unknown) {
	const amount = toNumber(value);
	if (typeof amount === 'number') {
		return `$${amount.toLocaleString()}`;
	}
	return typeof value === 'string' ? value : undefined;
}

function formatWholeNumber(value: unknown) {
	const amount = toNumber(value);
	if (typeof amount === 'number') {
		return amount.toLocaleString();
	}
	return typeof value === 'string' ? value : undefined;
}

function firstListingImage(listing?: AttachedListingData | null) {
	if (!listing) return undefined;

	const imageData = Array.isArray(listing.image_data)
		? listing.image_data.find(item => typeof item === 'string' && item.trim() !== '')
		: undefined;

	return (
		imageData ||
		(typeof listing.image === 'string' ? listing.image : undefined) ||
		(typeof listing.imagePath === 'string' ? listing.imagePath : undefined) ||
		(typeof listing.imageUrl === 'string' ? listing.imageUrl : undefined) ||
		(typeof listing.photo === 'string' ? listing.photo : undefined) ||
		(typeof listing.listingImage === 'string' ? listing.listingImage : undefined) ||
		(typeof listing.mainImage === 'string' ? listing.mainImage : undefined) ||
		(typeof listing.primaryImage === 'string' ? listing.primaryImage : undefined)
	);
}

function normalizeListing(listing?: AttachedListingData | null) {
	if (!listing) return null;

	const primaryImage = firstListingImage(listing);
	const formattedPrice = formatCurrency(listing.listPrice ?? listing.list_price);
	const formattedSqFoot = formatWholeNumber(listing.sqFoot ?? listing.sq_foot);

	// The raw `url` column is a path only ("/homes-for-sale-details/..."). read()'s
	// compose pass emits the absolute link as `listingurl`; without this preference
	// the direct `url` hit answers {{listing.url}} before the dictionary's
	// listingurl translation is ever consulted, so the tag rendered a domain-less
	// path everywhere it was used.
	const composedListingUrl =
		typeof listing.listingurl === 'string' && /^https?:\/\//i.test(listing.listingurl.trim())
			? listing.listingurl.trim()
			: undefined;

	return {
		...listing,
		url:
			composedListingUrl ||
			(typeof listing.url === 'string' ? listing.url : undefined),
		stateProvince:
			(typeof listing.stateProvince === 'string' && listing.stateProvince) ||
			(typeof listing.state === 'string' ? listing.state : undefined),
		state:
			(typeof listing.state === 'string' && listing.state) ||
			(typeof listing.stateProvince === 'string' ? listing.stateProvince : undefined),
		postalCode:
			(typeof listing.postalCode === 'string' && listing.postalCode) ||
			(typeof listing.zip === 'string' ? listing.zip : undefined),
		zip:
			(typeof listing.zip === 'string' && listing.zip) ||
			(typeof listing.postalCode === 'string' ? listing.postalCode : undefined),
		listPrice: formattedPrice,
		list_price: listing.list_price ?? listing.listPrice,
		sqFoot: formattedSqFoot,
		sq_foot: listing.sq_foot ?? listing.sqFoot,
		image_data: primaryImage
			? [primaryImage, ...(listing.image_data || []).slice(1)]
			: listing.image_data,
		image: primaryImage,
		imagePath: primaryImage,
		imageUrl: primaryImage,
		photo: primaryImage,
		listingImage: primaryImage,
		mainImage: primaryImage,
		primaryImage,
	};
}

function stringifyResolvedValue(value: unknown) {
	if (value == null) return undefined;
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (Array.isArray(value)) {
		const firstValue = value.find(item => item != null);
		if (
			typeof firstValue === 'string' ||
			typeof firstValue === 'number' ||
			typeof firstValue === 'boolean'
		) {
			return String(firstValue);
		}
	}
	return undefined;
}

export interface ResolveAttachedListingOptions {
	/** The listing this block is bound to, or the one an ancestor pins. */
	listing?: AttachedListingData | null;
	/**
	 * The current viewer's real (Act-As-aware) profile, keyed by user.* merge-tag
	 * leaf — see EditorLayout.js's userProfile / Editor/index.tsx's
	 * realBrandOverrides. Resolved the same way as an attached listing:
	 * unconditionally on canvas, no previewMode gate, since there is always
	 * exactly one "current user" candidate — nothing to attach/select the way a
	 * listing has to be.
	 */
	userProfile?: Record<string, unknown> | null;
	/** Keep each resolved value in a non-editable span carrying its tag key. */
	lockResolved?: boolean;
	/**
	 * Wraps every value this resolves. The raster copy uses it to keep a replaced
	 * field inside its merge-field span, so real data still shows as dynamic;
	 * every other caller substitutes bare text and passes nothing.
	 */
	wrapResolved?: (value: string, tagKey: string) => string;
}

/**
 * The dataSource a block should hand its preview (test-variable) resolution.
 *
 * Recipient-real preview with a listing bound: listing.* answers come from the
 * real listing record only. A field the record does not carry falls to the
 * tag's own fallback or renders blank — never to sample test data, which
 * would preview a value the send can never produce. Dropping the `listing`
 * group is enough: every test-variable resolver reads listing.* through it.
 * With no listing bound, test data stays — it is the only data there is.
 */
export function getPreviewResolutionDataSource(
	dataSource: Record<string, any> | undefined,
	selectedListing?: AttachedListingData | null,
): Record<string, any> | undefined {
	if (!dataSource?.recipientPreview || !selectedListing) return dataSource;
	const { listing: _listing, ...rest } = dataSource;
	return rest;
}

/**
 * Which profile a `{{user.*}}` tag resolves against, for every surface that has
 * to agree on whether a dynamic field "has data".
 *
 * `realUserProfile` carries ONLY the signed-in (act-as aware) viewer's real
 * fields, so when it exists a blank key means "this account genuinely has no
 * photo" and must NOT fall through to sample data — otherwise the canvas would
 * promise imagery the send can never produce. When there is no real viewer at
 * all (Design Manager authors generically, `userProfile` is undefined for global
 * roles) the sample `user` group is the only data there is, and it answers.
 *
 * Shared because it has already drifted once: the Image and Button blocks each
 * had their own copy of this expression while FocusTooltip read
 * `realUserProfile` alone. In Design Manager that made the canvas resolve
 * {{user.profileImageCircle}} to the generic-agent photo while the warning icon,
 * looking at `undefined`, insisted the block had no data and no fallback — a
 * warning triangle sitting on top of a perfectly good picture.
 */
export function resolveViewerProfile(
	source: Record<string, any> | undefined | null,
): Record<string, unknown> | null {
	return source?.realUserProfile || source?.user || null;
}

/**
 * One options object, not positional arguments, and deliberately so.
 *
 * This used to take (template, listing, options, userProfile, wrapResolved). When
 * `options` was inserted ahead of `userProfile`, four call sites kept passing the
 * profile third. Nothing caught it: callers read the profile off `dataSource`,
 * which is typed `any`, so the wrong slot type-checked. The profile silently
 * arrived undefined, every {{user.*}} tag came back unresolved, and dynamic
 * profile photos and logos fell through to placeholders across the canvas, the
 * raster, and the fallback warning icon. Named keys make that class of mistake
 * impossible — a misspelling is a compile error, an omission is just a default.
 */
export function resolveAttachedListingMergeTags(
	template: string | undefined,
	options: ResolveAttachedListingOptions = {},
) {
	const { listing: selectedListing, userProfile, wrapResolved } = options;

	if (!template) return template || '';
	if (!selectedListing && !userProfile) return template;

	const lockResolved = options.lockResolved === true;

	const normalizedListing = selectedListing ? normalizeListing(selectedListing) : null;

	// Most fields resolve directly off the normalized listing (path === dictionary key,
	// e.g. `zip`, `image_data`). Fields whose real CFC column name differs from the
	// dictionary key (e.g. `listing.mlsNumber` -> raw `ml_number`) fall back to the
	// dictionary's translation so the raw row can still answer them.
	// A genuinely blank resolved value (whitespace-only included) is not a
	// resolved value — undefined lets the caller fall through to its own
	// fallback/placeholder handling, same as an unresolved tag.
	const blankToUndefined = (value: string | undefined) => {
		if (value === undefined) return undefined;
		const trimmed = value.trim();
		return trimmed ? trimmed : undefined;
	};

	const resolveByPath = (path: string) => {
		const direct = get(normalizedListing, path);
		if (direct !== undefined) return blankToUndefined(stringifyResolvedValue(direct));

		const translation = getMergeTagTranslation(`listing.${path}`);
		const translatedPath = translation.startsWith('listing.')
			? translation.slice('listing.'.length)
			: translation;
		if (translatedPath === path) return undefined;

		return blankToUndefined(stringifyResolvedValue(get(normalizedListing, translatedPath)));
	};

	const resolveListingValue = (fullKey: string) => {
		const key = String(fullKey || '').trim();
		if (!key.startsWith('listing.') || !normalizedListing) return undefined;
		return resolveByPath(key.slice('listing.'.length));
	};

	const resolveUserValue = (fullKey: string) => {
		const key = String(fullKey || '').trim();
		if (!key.startsWith('user.') || !userProfile) return undefined;
		return blankToUndefined(stringifyResolvedValue(get(userProfile, key.slice('user.'.length))));
	};

	const resolveDynamicValue = (fullKey: string) => {
		const key = String(fullKey || '').trim();
		return key.startsWith('listing.') ? resolveListingValue(key) : resolveUserValue(key);
	};

	// Whether THIS call carries a data source for the tag's group. An {{#if}}
	// block whose group has no source here must be left intact for the
	// downstream resolver (the preview modal's test data, EmailPreview's real
	// Handlebars, the send pipeline) — collapsing it to its fallback would
	// discard a value a later pass could still resolve.
	const canAnswerKey = (key: string) =>
		key.startsWith('listing.') ? Boolean(normalizedListing) : Boolean(userProfile);

	// Wraps every value this resolves. The raster copy uses it to keep a replaced
	// field inside its merge-field span, so real data still shows as dynamic;
	// every other caller substitutes bare text and passes nothing.
	const emit = (value: string, tagKey: string) =>
		wrapResolved ? wrapResolved(value, tagKey) : value;

	// Defensive stash: a locked span's own resolved VALUE (real listing data,
	// not the {{...}} tag — that's never stored, see LOCKED_SPAN_PATTERN
	// above) could in principle contain "{{" if a listing field's real data
	// happened to include it. Protecting already-locked spans before any
	// pass runs keeps this function idempotent regardless.
	const vault: string[] = [];
	const stash = (html: string) => {
		const token = ` LOCK${vault.length} `;
		vault.push(html);
		return token;
	};

	let resolvedTemplate = template.replace(LOCKED_SPAN_PATTERN, matchedSpan => stash(matchedSpan));

	resolvedTemplate = resolvedTemplate.replace(
		ATTACHED_LISTING_IF_PATTERN,
		(
			match,
			conditionKey: string,
			trueExpression: string,
			fallbackText: string,
			offset: number,
			fullString: string,
		) => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const tagKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!isDynamicKey(tagKey) || !canAnswerKey(tagKey)) return match;

			const resolvedRawValue = resolveDynamicValue(tagKey);
			if (resolvedRawValue !== undefined && resolvedRawValue !== '') {
				const formattedValue = parsedHelper
					? formatDynamicTextValue(
							resolvedRawValue,
							parsedHelper.format,
							parsedHelper.maxLength,
						)
					: String(resolvedRawValue);
				const emittedValue = emit(formattedValue, tagKey);
				const shouldLock = lockResolved && !isInsideAttributeValue(fullString, offset);
				return stash(shouldLock ? lockResolvedValue(tagKey, emittedValue) : emittedValue);
			}

			// The raster copy — the only caller that passes wrapResolved — keeps the
			// whole {{#if}} block instead. bay-ui wraps the surviving token in the
			// merge-field span and the raster service fills it from source.context,
			// degrading to the field NAME. Substituting the send-time fallback here
			// put "Friend" in a thumbnail where "First Name" belonged, with no
			// underline, for every block that had a listing bound.
			if (wrapResolved) return match;

			return String(fallbackText || '').trim();
		},
	);

	// Whatever the narrow pass above couldn't match (a true-branch that isn't a
	// single bare tag) lands here. For the raster copy (wrapResolved), we keep the
 	// {{#if}} block itself intact for downstream handling; nested merge tags inside
 	// it may still be resolved by the later merge-tag passes below. Every other
	// caller gets the condition key resolved directly and the matching branch kept
	// as-is (tags still inside); the GENERIC_MERGE_TAG_PATTERN/ATTACHED_DYNAMIC_TAG_PATTERN
	// passes below then resolve whatever tags that surviving branch still contains.
	resolvedTemplate = resolvedTemplate.replace(
		ATTACHED_LISTING_IF_HTML_PATTERN,
		(match, conditionKey: string, trueBranch: string, falseBranch: string) => {
			if (wrapResolved) return match;

			const tagKey = String(conditionKey || '').trim();
			if (!isDynamicKey(tagKey) || !canAnswerKey(tagKey)) return match;

			const resolvedRawValue = resolveDynamicValue(tagKey);
			const isTruthy = resolvedRawValue !== undefined && resolvedRawValue !== '';
			return isTruthy ? trueBranch : falseBranch || '';
		},
	);

	resolvedTemplate = resolvedTemplate.replace(
		GENERIC_MERGE_TAG_PATTERN,
		(match, rawExpression: string, offset: number, fullString: string) => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(rawExpression || '').trim(),
			);
			if (!parsedHelper || !isDynamicKey(parsedHelper.key)) return match;

			// An empty-string resolution (a real field that's genuinely blank) must
			// fall through to the tag's own placeholder/fallback handling upstream,
			// same as an unresolved tag — never render as invisible/blank.
			const resolvedRawValue = resolveDynamicValue(parsedHelper.key);
			if (resolvedRawValue === undefined || resolvedRawValue === '') return match;

			const formattedValue = formatDynamicTextValue(
				resolvedRawValue,
				parsedHelper.format,
				parsedHelper.maxLength,
			);
			const emittedValue = emit(formattedValue, parsedHelper.key);
			const shouldLock = lockResolved && !isInsideAttributeValue(fullString, offset);
			return stash(
				shouldLock ? lockResolvedValue(parsedHelper.key, emittedValue) : emittedValue,
			);
		},
	);

	resolvedTemplate = resolvedTemplate.replace(
		ATTACHED_DYNAMIC_TAG_PATTERN,
		(match, _rawStart, fullKey: string, _rawEnd, offset: number, fullString: string) => {
			// Same rule as above: a genuinely blank real value is not a resolved
			// value — leave the raw tag for the caller's own fallback/placeholder
			// handling rather than rendering nothing.
			const resolvedValue = resolveDynamicValue(fullKey);
			if (!resolvedValue) return match;

			const emittedValue = emit(resolvedValue, fullKey);
			const shouldLock = lockResolved && !isInsideAttributeValue(fullString, offset);
			return stash(shouldLock ? lockResolvedValue(fullKey, emittedValue) : emittedValue);
		},
	);

	vault.forEach((html, index) => {
		resolvedTemplate = resolvedTemplate.split(` LOCK${index} `).join(html);
	});

	return resolvedTemplate;
}
